import clsx from 'clsx';
import { LayoutGrid } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';
import { EdgeGroupListItemResponse } from '@/react/edge/edge-groups/queries/useEdgeGroups';
import { useIsEdgeAdmin } from '@/react/hooks/useUser';

import { Badge } from '@@/Badge';
import { Checkbox } from '@@/form-components/Checkbox';
import { Link } from '@@/Link';
import { Loading } from '@@/Widget';
import { Card } from '@@/primitives/Card';

import { EdgeGroupId } from '../../types';
import { DeploymentType } from '../../detectDeploymentType';

import { useEdgeGroupsForDeploymentType } from './useEdgeGroupsForDeploymentType';
import { getEdgeGroupPlatformBadge } from './getEdgeGroupPlatformBadge';

export function TargetsSelector({
  values,
  onChange,
  deploymentType,
}: {
  values: Array<EdgeGroupId>;
  onChange: (next: Array<EdgeGroupId>) => void;
  deploymentType: DeploymentType;
}) {
  const { data, isLoading } = useEdgeGroupsForDeploymentType(deploymentType);
  const { isAdmin: isEdgeAdmin } = useIsEdgeAdmin();

  return (
    <Card.Container>
      <Card.Header
        title="Deployment targets"
        icon={LayoutGrid}
        actions={values.length ? <span>{values.length} selected</span> : null}
      />
      <Card.Body>
        {isLoading && <Loading />}
        {!isLoading && !data?.length && (
          <span>
            No edge groups available for this deployment type.{' '}
            {isEdgeAdmin ? (
              <>
                Head over to the{' '}
                <Link to="edge.groups.new" data-cy="create-edge-group-link">
                  Create edge group
                </Link>{' '}
                page to create one.
              </>
            ) : (
              'Contact an administrator to create an edge group for this deployment type.'
            )}
          </span>
        )}
        {!isLoading &&
          data &&
          data.map((group) => (
            <TargetRow
              key={group.Id}
              group={group}
              isSelected={values.includes(group.Id)}
              onToggle={(checked) => toggleTarget(group.Id, checked)}
            />
          ))}
      </Card.Body>
    </Card.Container>
  );

  function toggleTarget(id: EdgeGroupId, checked: boolean) {
    onChange(checked ? [...values, id] : values.filter((t) => t !== id));
  }
}

function TargetRow({
  group,
  isSelected,
  onToggle,
}: {
  group: EdgeGroupListItemResponse;
  isSelected: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const platformBadge = getEdgeGroupPlatformBadge(group.EndpointTypes);

  return (
    <label
      className={clsx(
        'mb-2 flex cursor-pointer items-center gap-3 rounded-md border border-solid p-3 last:mb-0',
        isSelected
          ? 'border-blue-6 bg-blue-1 th-highcontrast:border-blue-7 th-highcontrast:bg-blue-10 th-dark:border-blue-7 th-dark:bg-blue-10'
          : 'border-gray-5 bg-gray-2 th-highcontrast:border-gray-8 th-highcontrast:bg-black th-dark:border-gray-8 th-dark:bg-gray-10'
      )}
    >
      <Checkbox
        data-cy={`edge-group-select-${group.Name}`}
        id={group.Name}
        checked={isSelected}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <span className="font-medium">{group.Name}</span>
      <Badge type={platformBadge.type} size="sm">
        {platformBadge.label}
      </Badge>
      <span className="ml-auto text-xs text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
        {group.EndpointsCount}&nbsp;
        {pluralize(group.EndpointsCount, 'Environment')}
      </span>
    </label>
  );
}
